import type { GridState } from 'ag-grid-community';
import { createContext, useContext } from 'react';
import type { ColumnDynamics } from './columns/types';
import type { BlotterColumnState, BlotterTableFilter, BlotterTableSort, RowGroupsOpenedState } from './types';

/**
 * Defines the state of row group expansion in a blotter. Works for both standard row grouping and tree blotters. This is used
 * in place of AgGrid's own rowGroupExpansion GridState.
 *
 * A blotter is always in one of two base states: either all-expanded, or all-collapsed. There are then exceptions to this base states.
 * If the blotter is in all-expanded state, the exceptions listed are the collapsed row group node IDs, for example.
 */
export interface RowGroupExpansionState {
  expansionState: 'all-expanded' | 'all-collapsed';
  expansionExceptions: string[];
}

export interface ExtraGridState {
  /** Tracks row group expansion as a global default with per-node exceptions. */
  rowGroupExpansion?: RowGroupExpansionState;
  /** Tracks the existence of column dynamics along with their Spec type and any settings that are set on the column */
  columnDynamics?: ColumnDynamics[];
}

/** Persisted data for a single blotter (grid, filter, and Talos-only grid metadata). */
export type BlotterTableState = {
  gridState?: GridState;
  filter?: BlotterTableFilter;
  extraGridState?: ExtraGridState;
};

/** Full map of all persisted blotters (e.g. redux `blotters3`, WL user config `blotters3.*`). */
export type BlotterTableStorageMap = {
  [blotterId: string]: BlotterTableState;
};

/** @deprecated - we have migrated away from this state model to {@link BlotterTableStorageMap} / {@link BlotterTableState} */
export type BlotterTableStateV2 = {
  [blotterID: string]: {
    columns?: BlotterColumnState[];
    sort?: BlotterTableSort;
    filter?: BlotterTableFilter;
    rowGroupsOpened?: RowGroupsOpenedState;
    migratedOutDate?: number;
  };
};

export const BlotterTableStorageContext = createContext<BlotterTableStorageContextProps | undefined>(undefined);
BlotterTableStorageContext.displayName = 'BlotterTableStorage';

export function useBlotterTableStorage() {
  const context = useContext(BlotterTableStorageContext);
  if (context === undefined) {
    throw new Error('Missing BlotterTableStorageContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export interface BlotterTableStorageContextProps {
  getState(blotterID: string): BlotterTableState | undefined;
  setGridState(blotterID: string, gridState: GridState | undefined): void;
  setFilterState(blotterID: string, filterState: BlotterTableFilter | undefined): void;
  setExtraGridState(blotterID: string, extraGridState: ExtraGridState | undefined): void;

  cloneState(fromBlotterID: string, toBlotterID: string): void;

  /** Extreme behavior: Clear storage state of all blotters - Only used in Principal cases,
   * TODO: Can we remove this now that 'Reset columns' has support */
  clearAllBlotterState(): void;
}
